import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditDeveloperGuideDialog from "../../components/product-manager/EditDeveloperGuideDialog";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useDeveloperGuides } from "../../hooks/useDeveloperGuides";
import { apiService } from "../../api/services/apiService";
import type { DeveloperGuide } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

const DeveloperGuideHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { developerGuides: fetched, loading, error } = useDeveloperGuides();
  const [guides, setGuides] = useState<DeveloperGuide[]>([]);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setGuides(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () => filterByHubState(guides, hubState),
    [guides, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editGuide = useMemo(
    () => guides.find((g) => g.uuid === hubState.editUuid) ?? null,
    [guides, hubState.editUuid],
  );

  const handleToggle = async (guide: DeveloperGuide) => {
    setToggleLoadingId(guide.uuid);
    const newStatus =
      guide.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateDeveloperGuide(guide.uuid, {
        name: guide.name,
        description: guide.description ?? undefined,
        status: newStatus,
        sections: guide.sections,
        apiUuids: guide.apis?.map((a) => a.uuid),
        productUuids: guide.products?.map((p) => p.uuid),
      });
      setGuides((prev) =>
        prev.map((g) => (g.uuid === updated.uuid ? updated : g)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Developer Guides"
        hubDescription="Manage API developer guides with sections, code samples, and linked APIs and products."
        addPath={pmHubAddPath("developerGuide")}
        addLabel="Add Developer Guide"
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
      >
        <Grid container spacing={2}>
          {paginated.map((guide) => (
            <Grid item xs={12} sm={6} md={4} key={guide.uuid}>
              <EntityAssetCard
                title={guide.name}
                subtitle={guide.description ?? undefined}
                status={guide.status}
                enabled={guide.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === guide.uuid}
                onToggleEnabled={() => handleToggle(guide)}
                onEdit={() => hubState.setEditUuid(guide.uuid)}
                onDelete={async () => {
                  await apiService.deleteDeveloperGuide(guide.uuid);
                  setGuides((prev) => prev.filter((g) => g.uuid !== guide.uuid));
                }}
                metadata={
                  <Typography variant="caption" color="text.secondary">
                    {guide.sections?.length ?? 0} sections ·{" "}
                    {guide.apis?.length ?? 0} APIs ·{" "}
                    {guide.products?.length ?? 0} products
                  </Typography>
                }
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No developer guides match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <EditDeveloperGuideDialog
        guide={editGuide}
        open={Boolean(hubState.editUuid && editGuide)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setGuides((prev) =>
            prev.map((g) => (g.uuid === updated.uuid ? updated : g)),
          )
        }
      />
    </>
  );
};

export default DeveloperGuideHubPage;
